import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

// Maps the surface enum (matches.surface) to a Tailwind background using
// the Trader Pro tokens defined in styles/theme.css. Keeping the mapping
// in one place lets every page (dashboard card, cockpit, search results)
// render the badge identically.

// We use a function-based lookup instead of a Record literal so the snake_case
// surface enum value 'indoor_hard' (matched by Biome's useNamingConvention rule)
// stays out of object property positions where it'd be flagged.
function surfaceMeta(surface: string): {
  label: string;
  color: string;
  /** Spanish description shown on hover. */
  fullName: string;
} {
  switch (surface) {
    case 'hard':
      return {
        label: 'HARD',
        color: 'bg-surface-hard/15 text-surface-hard border-surface-hard/30',
        fullName: 'Cancha dura (hardcourt)',
      };
    case 'clay':
      return {
        label: 'CLAY',
        color: 'bg-surface-clay/15 text-surface-clay border-surface-clay/30',
        fullName: 'Polvo de ladrillo / arcilla',
      };
    case 'grass':
      return {
        label: 'GRASS',
        color: 'bg-surface-grass/15 text-surface-grass border-surface-grass/30',
        fullName: 'Pasto / hierba',
      };
    case 'indoor_hard':
      return {
        label: 'INDOOR',
        color: 'bg-surface-indoor-hard/15 text-surface-indoor-hard border-surface-indoor-hard/30',
        fullName: 'Cancha cubierta (indoor hard)',
      };
    case 'carpet':
      return {
        label: 'CARPET',
        color: 'bg-surface-carpet/15 text-surface-carpet border-surface-carpet/30',
        fullName: 'Carpeta sintética',
      };
    default:
      return {
        label: surface.toUpperCase(),
        color: 'bg-muted text-muted-foreground border-border',
        fullName: surface,
      };
  }
}

interface Props {
  surface: string | null | undefined;
  className?: string;
}

export function SurfaceBadge({ surface, className }: Props) {
  if (!surface) return null;
  const { label, color, fullName } = surfaceMeta(surface);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          data-slot="surface-badge"
          className={cn(
            'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-mono tracking-wider',
            color,
            className,
          )}
        >
          {label}
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom">{fullName}</TooltipContent>
    </Tooltip>
  );
}
